import { type ReactNode, useEffect, useId, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PERMISSIONS, type ProductResponse, type ShopResponse } from '@aps/contracts';
import { Button, useToast } from '@aps/ui';
import { ApiError, api } from '../lib/api.js';
import { messages } from '../i18n/messages.js';
import { useSession } from '../app/session-context.js';
import { can } from '../lib/session.js';

interface StockPanelProps {
  product: ProductResponse;
  /** Formadagi (hali saqlanmagan bo'lishi mumkin) ta'minot turi. */
  supplyType: 'IN_STOCK' | 'PRE_ORDER';
}

/** Har do'kon uchun joriy holat: bron = qoldiq - mavjud. */
function currentOf(product: ProductResponse, shopId: string): { qty: number; reserved: number } {
  const row = product.stock.find((entry) => entry.shopId === shopId);
  return row === undefined
    ? { qty: 0, reserved: 0 }
    : { qty: row.qty, reserved: Math.max(0, row.qty - row.available) };
}

/**
 * Do'konlardagi qoldiq (faqat `IN_STOCK`).
 *
 * Odam YANGI qoldiqni yozadi, server esa FARQni qabul qiladi
 * (`POST products/:id/stock`, har o'zgarish `StockMove` ga yoziladi).
 * Farq shu yerda hisoblanadi. Buyurtma asosidagi (`PRE_ORDER`) tovarda
 * qoldiq yuritilmaydi — panel buni aytadi va maydon ko'rsatmaydi.
 */
export function StockPanel({ product, supplyType }: StockPanelProps): ReactNode {
  const { locale, me } = useSession();
  const t = messages(locale);
  const client = useQueryClient();
  const toast = useToast();
  const noteId = useId();

  const canManage = can(me, PERMISSIONS.STOCK_MANAGE);
  const shops = useQuery({ queryKey: ['shops'], queryFn: () => api.shops() });

  const [values, setValues] = useState<Record<string, string>>({});
  const [note, setNote] = useState(t.stockNoteDefault);
  const [error, setError] = useState<string | null>(null);

  // Saqlangandan keyin (yangi qoldiq kelganda) kataklar joriy qiymatga qaytadi.
  useEffect(() => {
    const next: Record<string, string> = {};
    for (const shop of shops.data ?? []) next[shop.id] = String(currentOf(product, shop.id).qty);
    setValues(next);
  }, [product, shops.data]);

  const changes = (shops.data ?? []).flatMap((shop: ShopResponse) => {
    const raw = values[shop.id];
    if (raw === undefined || raw.trim() === '') return [];
    const target = Number.parseInt(raw, 10);
    const delta = target - currentOf(product, shop.id).qty;
    return Number.isInteger(target) && delta !== 0 ? [{ shopId: shop.id, qty: delta }] : [];
  });

  const save = useMutation({
    mutationFn: async () => {
      // Ketma-ket: har o'zgarish o'z tranzaksiyasida va o'z jurnal yozuvi bilan.
      for (const change of changes) {
        await api.adjustStock(product.id, { ...change, note: note.trim() || t.stockNoteDefault });
      }
    },
    onSuccess: () => {
      setError(null);
      toast.show(t.stockSaved, 'success');
      void client.invalidateQueries({ queryKey: ['admin-product', product.id] });
      void client.invalidateQueries({ queryKey: ['admin-products'] });
    },
    onError: (cause: unknown) => {
      const text = cause instanceof ApiError ? cause.text(locale) : t.errorTitle;
      setError(text);
      // Bir qismi o'tgan bo'lishi mumkin — joriy holat qayta o'qiladi.
      void client.invalidateQueries({ queryKey: ['admin-product', product.id] });
    },
  });

  if (supplyType === 'PRE_ORDER') {
    return <p className="aps-muted">{t.stockNotTracked}</p>;
  }
  if (product.supplyType !== 'IN_STOCK') {
    return <p className="aps-muted">{t.stockSaveFirst}</p>;
  }

  return (
    <div className="adm-stock">
      <table className="adm-stock__table">
        <thead>
          <tr>
            <th scope="col">{t.stockShop}</th>
            <th scope="col">{t.stockNow}</th>
            <th scope="col">{t.stockReserved}</th>
            <th scope="col">{t.stockNew}</th>
          </tr>
        </thead>
        <tbody>
          {(shops.data ?? []).map((shop) => {
            const current = currentOf(product, shop.id);
            return (
              <tr key={shop.id}>
                <th scope="row">{locale === 'RU' ? (shop.nameRu ?? shop.nameUz) : shop.nameUz}</th>
                <td>{current.qty}</td>
                <td>{current.reserved}</td>
                <td>
                  <input
                    className="aps-field__input adm-stock__input"
                    inputMode="numeric"
                    aria-label={`${t.stockNew}: ${shop.nameUz}`}
                    value={values[shop.id] ?? ''}
                    disabled={!canManage}
                    onChange={(event) => {
                      setValues((state) => ({
                        ...state,
                        [shop.id]: event.target.value.replace(/\D/gu, ''),
                      }));
                    }}
                  />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <p className="aps-field__hint">{t.stockZeroHint}</p>

      {canManage && (
        <>
          <div className="aps-field">
            <label className="aps-field__label" htmlFor={noteId}>
              {t.stockNote}
            </label>
            <input
              id={noteId}
              className="aps-field__input"
              value={note}
              maxLength={255}
              onChange={(event) => {
                setNote(event.target.value);
              }}
            />
          </div>
          {error !== null && <p role="alert">{error}</p>}
          <Button
            variant="secondary"
            disabled={changes.length === 0 || save.isPending}
            onClick={() => {
              save.mutate();
            }}
          >
            {changes.length === 0 ? t.stockNoChange : t.stockSave}
          </Button>
        </>
      )}
    </div>
  );
}
